import type { YinshState } from "@cloudnest/yinsh-engine";

/**
 * The six UI phases of the in-game screen. This is a presentation projection
 * of the engine's four phases (`RING_PLACEMENT | PLAYING | REMOVING_ROW |
 * GAME_END`) plus the UI-only selection sub-state — it is NOT an engine
 * concept. Chip copy, hints, and phase-specific affordances key off this.
 * Contract: docs/wireframes/yinsh/specs/active-game.md (phase-state matrix).
 */
export type UiPhase =
  | "PLACING_RINGS"
  | "SELECT_RING"
  | "MOVING_RING"
  | "SELECT_ROW"
  | "REMOVE_RING"
  | "END";

/**
 * Map engine state + the UI ring selection to a single UI phase.
 *
 * `selectedRingPosition` is client-only store state, so the MOVING_RING vs
 * SELECT_RING distinction lives here rather than in the engine. The
 * SELECT_ROW vs REMOVE_RING split mirrors the engine's own sub-state:
 * `pendingRows` is non-empty exactly while a 5-row still needs scoring.
 */
export function derivePhase(
  gameState: YinshState,
  selectedRingPosition: string | null,
): UiPhase {
  switch (gameState.phase) {
    case "RING_PLACEMENT":
      return "PLACING_RINGS";
    case "PLAYING":
      return selectedRingPosition !== null ? "MOVING_RING" : "SELECT_RING";
    case "REMOVING_ROW":
      return gameState.pendingRows.length > 0 ? "SELECT_ROW" : "REMOVE_RING";
    case "GAME_END":
      return "END";
  }
}
